import { ReactElement } from 'react';

import create from 'zustand';

import { IconProps } from '@components/icons';
import { randomId } from '@utils/strings';

type CustomButton = {
  text: string;
  icon?: ReactElement<IconProps>;
  onClick: () => void;
};

type ToastBase = {
  id: ReturnType<typeof randomId>;
  title: string;
  subtitle?: string;
  url?: string;
  customButtons?: CustomButton[];
  duration?: number;
};

type GeneralToast = ToastBase & {
  variant: 'info' | 'success';
};

export type ErrorToast = ToastBase & {
  variant: 'error';
  resolve: () => void;
  retry: () => void;
};

export type Toast = GeneralToast | ErrorToast;

export const isToastError = (toast: Toast): toast is ErrorToast => {
  const toastAsError = toast as ErrorToast;
  return Boolean(toastAsError.resolve || toastAsError.retry);
};

const defaultState = {
  toasts: [] as Toast[],
};

export const useToastStore = create(() => defaultState);

export const remove = (toastId: Toast['id']) =>
  useToastStore.setState((state) => ({
    toasts: state.toasts.filter((toast) => toast.id !== toastId),
  }));

const showToast = ({
  title,
  options = {},
  variant,
}: {
  title: Toast['title'];
  variant: Toast['variant'];
  options?: Partial<Toast>;
}) => {
  const toast = { id: randomId(), variant, title, ...options } as Toast;
  useToastStore.setState((state) => {
    if (toast.id && state.toasts.some((t) => t.id === toast.id)) return state;
    return { toasts: [...state.toasts, toast] };
  });
};

type ToastOptions<ToastType extends Toast> = Omit<Partial<ToastType>, 'variant'>;

export const showSuccess = (title: Toast['title'], options?: ToastOptions<GeneralToast>) =>
  showToast({ title, options, variant: 'success' });
export const showInfo = (title: Toast['title'], options?: ToastOptions<GeneralToast>) =>
  showToast({ title, options, variant: 'info' });
export const showError = (title: Toast['title'], options?: ToastOptions<ErrorToast>) =>
  showToast({ title, options, variant: 'error' });

export const useToaster = () => {
  return { showSuccess, showError, showInfo, remove };
};
